import { createTheme } from '@mui/material/styles';
import { createAtlasTheme, type AtlasThemeResolved } from './atlas';
import { atlasSemantic } from './atlas/tokens';
import { vaultVariantTheme } from './atlas/vaultVariant';

function chartHexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = Number.parseInt(h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const vaultChartPalette = {
  ...atlasSemantic.color.dataVisualization.chart,
  neutral: '#E4E5E6',
  white: '#FFFFFF',
} as const;

/* eslint quotes: "off" */

export const themeOptions = {
  typography: {
    fontFamily: '"Ubuntu", sans-serif',
    body1: {
      fontFamily: '"Ubuntu", sans-serif',
    },
    h4: {
      fontFamily: '"Ubuntu", sans-serif',
      fontWeight: 400,
    },
    h6: {
      fontFamily: '"Ubuntu", sans-serif',
      fontWeight: 400,
    },
    subtitle1: {
      fontFamily: '"Ubuntu", sans-serif',
    },
    button: {
      fontFamily: '"Ubuntu", sans-serif',
      fontWeight: 400,
    },
  },
  // replace with newPalette object
  palette: {
    primary: {
      light: '#464e53',
      main: '#282e37',
      dark: '#121a26',
    },
    secondary: {
      main: '#19142F',
    },
    success: {
      main: '#8ee400',
    },
    error: {
      light: '#d3222a',
      main: '#921a1d',
      dark: '#5f091d',
    },
    warning: {
      light: '#fee400',
      main: '#fee400',
      dark: '#6a5f00',
    },
    info: {
      light: '#1dd9f9',
      main: '#1dd9f9',
      dark: '#0b4cce',
    },
    draft: {
      main: '#415587',
      text: '#18162E',
    },
    grey: {
      50: '#F8FBFC',
      100: '#F5F5F5',
      200: '#D0D0D0',
      400: '#C5C5C5',
      500: '#A0A0A0',
      600: '#8C8C8C',
      800: '#5B5B5B',
      900: '#3B3B3B',
      A100: '#90A2AB',
      E100: '#e5e9ec',
      D100: '#DADADA',
      B100: '#707070',
    },
    white: '#FFFFFF',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1240,
      lg: 1280,
      xl: 1920,
    },
  },
};

export enum FontWeights {
  'regular' = 400,
  'semibold' = 600,
}

export const newThemeOptions = {
  newPalette: {
    neutral: {
      N0: '#FFFFFF',
      N100: '#FAFBFF',
      N200: '#F2F2F3',
      N300: '#E4E5E6',
      N400: '#C6C8C9',
      N500: '#A2A6A8',
      N600: '#71717A',
      N700: '#546276',
      N800: '#17353D',
      N900: '#12292F',
      N1000: '#0A181B',
    },
    turquoise: {
      T100: '#D8EFEB',
      T200: '#81BBB1',
      T300: '#35A191',
      T400: '#168070',
      T500: '#096C5E',
    },
    green: {
      G100: '#BBDBC9',
      G200: '#A7ED24',
      G300: '#3EB572',
      G400: '#258750',
      G500: '#016533',
    },
    yellow: {
      Y100: '#FFF8F2',
      Y200: '#FFE3C7',
      Y300: '#FFC276',
      Y400: '#FFB020',
      Y500: '#A16F14',
      Y600: '#fff3a9',
    },
    red: {
      R100: '#FCF2F2',
      R200: '#FF5F5F',
      R300: '#E2342A',
      R400: '#BE2714',
      R500: '#8F211B',
    },
    chart: vaultChartPalette,
    accordion: {
      blue: chartHexToRgba(vaultChartPalette.blue, 0.15),
      yellow: chartHexToRgba(vaultChartPalette.yellow, 0.15),
      red: chartHexToRgba(vaultChartPalette.red, 0.15),
      green: chartHexToRgba(vaultChartPalette.green, 0.15),
      pink: chartHexToRgba(vaultChartPalette.pink, 0.15),
      teal: chartHexToRgba(vaultChartPalette.teal, 0.15),
      violet: chartHexToRgba(vaultChartPalette.violet, 0.15),
      yellow2: chartHexToRgba(vaultChartPalette.yellow2, 0.15),
      neutral: vaultChartPalette.neutral,
      white: vaultChartPalette.white,
    },
    accordionHover: {
      blue: chartHexToRgba(vaultChartPalette.blue, 0.3),
      yellow: chartHexToRgba(vaultChartPalette.yellow, 0.3),
      red: chartHexToRgba(vaultChartPalette.red, 0.3),
      green: chartHexToRgba(vaultChartPalette.green, 0.3),
      pink: chartHexToRgba(vaultChartPalette.pink, 0.3),
      teal: chartHexToRgba(vaultChartPalette.teal, 0.3),
      violet: chartHexToRgba(vaultChartPalette.violet, 0.3),
      yellow2: chartHexToRgba(vaultChartPalette.yellow2, 0.3),
      neutral: vaultChartPalette.neutral,
      white: vaultChartPalette.white,
    },
  },
  newTypography: {
    textStyleDisplaySpecial: {
      fontFamily: 'Oswald',
      fontWeight: FontWeights.semibold,
      fontSize: '42px',
      lineHeight: '52px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleDisplayXLarge: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '42px',
      lineHeight: '44px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleDisplayLarge: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '28px',
      lineHeight: '28px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleDisplayMedium: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.regular,
      fontSize: '24px',
      lineHeight: '26px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleDisplaySmall: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.regular,
      fontSize: '20px',
      lineHeight: '20px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleHeading: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleSubheading: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '1px',
      textTransform: 'uppercase' as 'none' | 'uppercase',
    },
    textStyleBodyLarge: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.regular,
      fontSize: '18px',
      lineHeight: '32px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleBodyLargeSemibold: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '18px',
      lineHeight: '32px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleBody: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.regular,
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleBodySemibold: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '14px',
      lineHeight: '24px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleBodySmall: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.regular,
      fontSize: '12px',
      lineHeight: '20px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
    textStyleBodySmallSemibold: {
      fontFamily: 'Rubik',
      fontWeight: FontWeights.semibold,
      fontSize: '12px',
      lineHeight: '20px',
      letterSpacing: 0,
      textTransform: 'none' as 'none' | 'uppercase',
    },
  },
  boxShadows: {
    shadowSmall: {
      boxShadow: '0px 1px 4px -6px rgba(0, 0, 0, 0.13)',
      filter: 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.4))',
    },
    shadowMedium: {
      boxShadow: '0px 7px 10px -6px rgba(0, 0, 0, 0.16)',
      filter: 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.4))',
    },
    shadowLarge: {
      boxShadow: '0px 6px 16px -6px rgba(0, 0, 0, 0.14)',
      filter: 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.4))',
    },
    shadowXLarge: {
      boxShadow:
        '0px 24px 24px -8px rgba(0, 0, 0, 0.08), 0px 0px 28px -8px rgba(0, 0, 0, 0.12)',
      filter: 'drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.2))',
    },
    focusState: `0px 0px 0px 4px #D8EFEB`,
  },
  newBreakpoints: {
    narrow: 480,
  },
};

export const colors = {
  ...newThemeOptions.newPalette.neutral,
  ...newThemeOptions.newPalette.turquoise,
  ...newThemeOptions.newPalette.green,
  ...newThemeOptions.newPalette.yellow,
  ...newThemeOptions.newPalette.red,
  ...newThemeOptions.newPalette.chart,
};

export type ThemeType = {
  [Key in keyof typeof themeOptions]: (typeof themeOptions)[Key];
};

export const atlas = createAtlasTheme('light');

export type AtlasTheme = AtlasThemeResolved;

export { createAtlasTheme } from './atlas';
export type { AtlasThemeMode } from './atlas';

export const theme = {
  ...themeOptions,
  ...newThemeOptions,
  atlas,
  isVaultVariant: false as boolean,
  spacing: (spacing?: number) => spacing ?? 8,
};

/**
 * Builds the full theme object for a given variant.
 * - 'atlas': Atlas design system colors + typography (FF ON)
 * - 'vault': Vault legacy colors + typography (FF OFF, OPR always uses this)
 *
 * Module-level constants avoid per-render recreation.
 */
const atlasThemeObject = {
  ...themeOptions,
  ...newThemeOptions,
  atlas,
  isVaultVariant: false as boolean,
  spacing: (spacing?: number) => spacing ?? 8,
};

const vaultThemeObject = {
  ...themeOptions,
  ...newThemeOptions,
  atlas: vaultVariantTheme,
  isVaultVariant: true as boolean,
  spacing: (spacing?: number) => spacing ?? 8,
};

export function buildTheme(variant: 'atlas' | 'vault') {
  return variant === 'atlas' ? atlasThemeObject : vaultThemeObject;
}

const legacyMuiThemeOptions = {
  ...themeOptions,
  props: {
    MuiFormControl: {
      variant: 'standard' as const,
    },
    MuiLink: {
      underline: 'hover' as const,
    },
    MuiSelect: {
      variant: 'standard' as const,
    },
    MuiTextField: {
      variant: 'standard' as const,
    },
  },
};

export const oldTheme = createTheme(legacyMuiThemeOptions);

export default theme;
